import { useState, useEffect, useRef } from 'react';
import { getEmployeeDetails, DETAIL_FIELDS as FIELDS } from '../lib/employeeDetails';
import { appState } from './AppStatus';

const fmtMoney = (n) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (d) => (d ? new Date(d + 'T12:00:00').toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' }) : '');
const seniority = (d) => {
  if (!d) return '';
  const a = new Date(d + 'T12:00:00'); const n = new Date();
  let m = (n.getFullYear() - a.getFullYear()) * 12 + n.getMonth() - a.getMonth(); if (n.getDate() < a.getDate()) m--;
  if (m < 1) return 'menos de un mes';
  const y = Math.floor(m / 12), r = m % 12;
  return [y ? `${y} ${y === 1 ? 'año' : 'años'}` : '', r ? `${r} ${r === 1 ? 'mes' : 'meses'}` : ''].filter(Boolean).join(' y ');
};
const inp = 'mt-1 w-full px-3 py-2 rounded-xl border border-gray-200 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-sky-200';

const Row = ({ k, v }) => (
  <div className="flex items-baseline justify-between gap-3 py-1.5">
    <span className="text-xs text-gray-400 shrink-0">{k}</span>
    <span className={`text-sm text-right break-words min-w-0 ${v ? 'text-gray-800' : 'text-gray-300'}`}>{v || '—'}</span>
  </div>
);
const Sec = ({ title, children }) => (
  <div className="rounded-2xl border border-gray-100 p-3.5">
    <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1">{title}</p>
    <div className="divide-y divide-gray-50">{children}</div>
  </div>
);
const F = ({ label, children }) => <label className="block text-xs text-gray-500">{label}{children}</label>;

// ctx: { locations, sedeIds, areas, canPay, canJpct, orgJpct, canEdit }
export default function EmployeeDatos({ emp, ctx, sedes = [], app, showToast, avatarUrl, onPhoto, onSave }) {
  const { locations = [], sedeIds = [], areas = [], canPay, canPayEdit, canJpct, orgJpct = 0, canEdit } = ctx;
  const fileRef = useRef(null);
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    getEmployeeDetails(emp.id).then((x) => { if (alive) setD(x); }).catch(() => { if (alive) setD({}); });
    return () => { alive = false; };
  }, [emp.id]);
  if (!d) return <p className="text-sm text-gray-400 text-center py-10">Cargando…</p>;

  const startEdit = () => {
    setF({
      name: emp.name || '', email: emp.email || '', password: '', active: emp.active !== false,
      pay_type: emp.pay_type === 'hourly' ? 'hourly' : 'fixed', area: emp.area || '', hire_date: emp.hire_date || '',
      location_ids: [...sedeIds],
      salary: emp.salary ?? '', monthly_hours: emp.monthly_hours ?? '', extra_hour_rate: emp.extra_hour_rate ?? '',
      hourly_rate: emp.hourly_rate ?? '', per_diem: emp.per_diem ?? '',
      jpct: emp.justified_deduct_pct == null ? '' : String(emp.justified_deduct_pct),
      details: { ...d },
    });
    setEdit(true);
  };
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (!f.name.trim() || !f.email.trim()) return showToast?.('Nombre y email son obligatorios', 'error');
    setBusy(true);
    try { await onSave(f); setD({ ...f.details }); setEdit(false); }
    catch (e) { showToast?.(e.message || 'No se pudo guardar', 'error'); }
    setBusy(false);
  };

  const hourly = emp.pay_type === 'hourly';
  const as = appState(app);
  const Photo = (
    <div className="flex items-center gap-3.5 px-1">
      {avatarUrl
        ? <img src={avatarUrl} alt={emp.name} className="w-16 h-16 rounded-3xl object-cover bg-gray-100" />
        : <div className="w-16 h-16 rounded-3xl flex items-center justify-center text-xl font-bold bg-sky-100 text-sky-700">{emp.avatar || '?'}</div>}
      <div className="min-w-0 flex-1">
        <p className="font-bold text-gray-900 truncate">{emp.name}</p>
        <p className="text-xs text-gray-400 truncate">{emp.email}</p>
        {canEdit && onPhoto && <button type="button" onClick={() => fileRef.current?.click()} className="mt-1 text-xs font-bold text-sky-700">{avatarUrl ? 'Cambiar foto' : 'Subir foto'}</button>}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const x = e.target.files?.[0]; e.target.value = ''; if (x) onPhoto(x); }} />
      </div>
      {canEdit && !edit && <button type="button" onClick={startEdit} className="shrink-0 px-3.5 py-2 rounded-xl text-xs font-bold text-sky-700 bg-sky-50">Editar ficha</button>}
    </div>
  );

  if (edit) {
    const locSel = f.location_ids || [];
    return (
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
        {Photo}
        <Sec title="Cuenta">
          <div className="space-y-2.5 py-1.5">
            <F label="Nombre completo"><input className={inp} value={f.name} onChange={(e) => set('name', e.target.value)} /></F>
            <F label="Email de ingreso"><input type="email" className={inp} value={f.email} onChange={(e) => set('email', e.target.value)} /></F>
            <F label="Nueva contraseña (opcional)"><input type="text" autoComplete="off" className={inp} value={f.password} placeholder="Vacío = no cambia" onChange={(e) => set('password', e.target.value)} /></F>
            <label className="flex items-center justify-between py-1"><span className="text-sm text-gray-700">Persona activa</span>
              <button type="button" role="switch" aria-checked={f.active} onClick={() => set('active', !f.active)} className={`w-10 h-6 rounded-full relative transition-colors ${f.active ? 'bg-sky-600' : 'bg-gray-200'}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all ${f.active ? 'left-[18px]' : 'left-0.5'}`} /></button></label>
          </div>
        </Sec>
        <Sec title="Trabajo">
          <div className="space-y-2.5 py-1.5">
            <div className="grid grid-cols-2 gap-2">
              {[['fixed', 'Sueldo fijo'], ['hourly', 'Por hora']].map(([v, l]) => (
                <button key={v} type="button" onClick={() => set('pay_type', v)} className={`py-2 rounded-xl border-2 text-sm font-bold ${f.pay_type === v ? 'border-sky-500 bg-sky-50 text-sky-800' : 'border-gray-100 text-gray-500'}`}>{l}</button>
              ))}
            </div>
            {areas.length > 0 && <F label="Perfil / área"><select className={inp} value={f.area} onChange={(e) => set('area', e.target.value)}><option value="">Sin área</option>{areas.map((a) => <option key={a} value={a}>{a}</option>)}</select></F>}
            <F label="Fecha de ingreso"><input type="date" className={inp} value={f.hire_date} onChange={(e) => set('hire_date', e.target.value)} /></F>
            {locations.length > 1 && (
              <div><p className="text-xs text-gray-500 mb-1">Sedes donde puede marcar <span className="text-gray-400">(ninguna = cualquiera)</span></p>
                <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                  {locations.map((l) => (
                    <label key={l.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer text-sm text-gray-800">
                      <input type="checkbox" checked={locSel.includes(l.id)} onChange={() => set('location_ids', locSel.includes(l.id) ? locSel.filter((x) => x !== l.id) : [...locSel, l.id])} />{l.name}
                    </label>))}
                </div></div>
            )}
          </div>
        </Sec>
        {canPayEdit && (
          <Sec title="Pago">
            <div className="space-y-2.5 py-1.5">
              {f.pay_type === 'fixed' ? (<>
                <F label="Sueldo base mensual (ARS)"><input type="number" min="0" className={inp} value={f.salary} onChange={(e) => set('salary', e.target.value)} /></F>
                <div className="grid grid-cols-2 gap-2">
                  <F label="Horas mensuales"><input type="number" min="0" className={inp} value={f.monthly_hours} onChange={(e) => set('monthly_hours', e.target.value)} /></F>
                  <F label="Valor hora extra"><input type="number" min="0" className={inp} value={f.extra_hour_rate} onChange={(e) => set('extra_hour_rate', e.target.value)} /></F>
                </div>
              </>) : (
                <div className="grid grid-cols-2 gap-2">
                  <F label="Valor por hora"><input type="number" min="0" className={inp} value={f.hourly_rate} placeholder="Del perfil" onChange={(e) => set('hourly_rate', e.target.value)} /></F>
                  <F label="Viático por día"><input type="number" min="0" className={inp} value={f.per_diem} placeholder="Del perfil" onChange={(e) => set('per_diem', e.target.value)} /></F>
                </div>
              )}
              {canJpct && <F label={`Descuento por falta justificada (%) — vacío = organización (${orgJpct}%)`}><input inputMode="decimal" className={inp} value={f.jpct} onChange={(e) => set('jpct', e.target.value.replace(/[^0-9.]/g, ''))} /></F>}
            </div>
          </Sec>
        )}
        <Sec title="Datos personales">
          <div className="space-y-2.5 py-1.5">
            {FIELDS.map(([k, l, t]) => (
              <F key={k} label={l}><input type={t || 'text'} className={inp} value={f.details[k] || ''} onChange={(e) => setF((x) => ({ ...x, details: { ...x.details, [k]: e.target.value } }))} /></F>
            ))}
          </div>
        </Sec>
        <div className="flex gap-2 sticky bottom-0 bg-white pt-2 pb-1">
          <button type="button" onClick={save} disabled={busy} className="flex-1 text-sm font-semibold text-white bg-sky-600 py-3 rounded-xl disabled:opacity-50">{busy ? 'Guardando…' : 'Guardar todo'}</button>
          <button type="button" onClick={() => setEdit(false)} className="px-5 text-sm font-semibold text-gray-500 bg-gray-100 rounded-xl">Cancelar</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
      {Photo}
      <Sec title="Datos personales">
        <Row k="Email" v={emp.email} />
        {FIELDS.map(([k, l, t]) => <Row key={k} k={l} v={t === 'date' ? fmtDate(d[k]) : d[k]} />)}
      </Sec>
      <Sec title="Trabajo">
        <Row k="Modalidad" v={hourly ? 'Por hora' : 'Sueldo fijo'} />
        <Row k="Área" v={emp.area} />
        <Row k="Sede" v={sedes.length ? sedes.join(' · ') : 'Cualquier sede'} />
        <Row k="Ingreso" v={emp.hire_date ? `${fmtDate(emp.hire_date)} · ${seniority(emp.hire_date)}` : ''} />
        <Row k="Estado" v={emp.active === false ? 'Inactivo' : 'Activo'} />
        {canPay && !hourly && <Row k="Sueldo base" v={emp.salary > 0 ? fmtMoney(emp.salary) + '/mes' : ''} />}
        {canPay && hourly && <Row k="Valor por hora" v={emp.hourly_rate > 0 ? fmtMoney(emp.hourly_rate) : 'Del perfil'} />}
        {canPay && hourly && <Row k="Viático por día" v={emp.per_diem > 0 ? fmtMoney(emp.per_diem) : 'Del perfil'} />}
        {canPay && !hourly && emp.monthly_hours > 0 && <Row k="Horas mensuales" v={`${emp.monthly_hours} h`} />}
        {canPay && !hourly && emp.extra_hour_rate > 0 && <Row k="Valor hora extra" v={fmtMoney(emp.extra_hour_rate)} />}
        {canJpct && <Row k="Descuento falta justificada" v={emp.justified_deduct_pct != null ? `${emp.justified_deduct_pct} %` : `Organización (${orgJpct} %)`} />}
      </Sec>
      <Sec title="Cuenta y app">
        <Row k="Huella / Face ID" v={emp.bio_registered ? 'Registrada' : 'Sin registrar'} />
        <Row k="App instalada" v={as.installed ? 'Sí' : 'No'} />
        {as.installed && <Row k="Notificaciones" v={as.push ? 'Activadas' : as.denied ? 'Bloqueadas' : 'Sin aceptar'} />}
        <Row k="Alta" v={fmtDate((emp.created_at || '').slice(0, 10))} />
      </Sec>
    </div>
  );
}
